// After a deploy, an open tab still runs the old build: a part of it loaded later (a code-split chunk, its CSS) is gone
// from the server and the import fails. One automatic reload brings the new build; sessionStorage remembers that this
// tab just reloaded for it, so a chunk that fails again shows the error instead of reloading in a loop.

const KEY = "kr.chunkReload.at";
/** No second automatic reload within this long of the first. */
export const RELOAD_GUARD_MS = 60_000;

const CHUNK_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed|Unable to preload CSS/i;

/** A failed load of a part of the app (not a bug in it). */
export function isChunkError(e: unknown): boolean {
  const text = e instanceof Error ? `${e.name} ${e.message}` : typeof e === "string" ? e : (e as { message?: unknown } | null)?.message;
  return typeof text === "string" && CHUNK_ERROR.test(text);
}

/** Whether this tab may reload now (and, if so, notes that it did). */
export function mayReload(storage: Pick<Storage, "getItem" | "setItem">, now: number): boolean {
  const last = Number(storage.getItem(KEY) ?? 0);
  if (last && now - last >= 0 && now - last < RELOAD_GUARD_MS) return false;
  storage.setItem(KEY, String(now));
  return true;
}

/** Reloads once for a stale chunk; false when it already did a moment ago (or storage is refused). */
export function reloadOnce(): boolean {
  try {
    if (!mayReload(sessionStorage, Date.now())) return false;
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Listens for chunk failures outside React's render (Vite's preload error, an unhandled import rejection). */
export function watchStaleChunks(): void {
  window.addEventListener("vite:preloadError", (e) => { if (reloadOnce()) e.preventDefault(); });
  window.addEventListener("unhandledrejection", (e) => { if (isChunkError(e.reason) && reloadOnce()) e.preventDefault(); });
}

/**
 * Loads a code-split part of the app (for React.lazy). If it fails because the build on the server changed, the tab
 * reloads once and the promise never settles, so the loading state stays until the new build is up; any other failure
 * (or a second one within the guard) goes on to the error boundary.
 */
export function loadChunk<T>(load: () => Promise<T>, reload: () => boolean = reloadOnce): Promise<T> {
  return load().catch((e: unknown) => {
    if (isChunkError(e) && reload()) return new Promise<T>(() => {});
    throw e;
  });
}
